import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Box,
  Badge,
  Checkbox,
  Divider,
  Spinner,
} from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { useShop } from '../context/ShopContext';
import { useAuthenticatedFetch } from '../hooks/useApi';

interface EmailConfiguration {
  senderEmail: string;
  senderDomain: string;
  verified: boolean;
  postPurchaseEnabled: boolean;
  abandonedCartEnabled: boolean;
  hideSentEmails?: boolean;
  lastVerified?: string;
}

export function EmailSettings() {
  const { shop } = useShop();
  const fetchWithAuth = useAuthenticatedFetch();
  const { t } = useTranslation();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [config, setConfig] = useState<EmailConfiguration | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch email configuration
  const fetchConfig = useCallback(async () => {
    if (!shop) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await fetchWithAuth('/api/email/settings') as EmailConfiguration;
      setConfig(response);
    } catch (err) {
      console.error('Error fetching email config:', err);
      // If no config exists, that's okay - they need to set it up
      setConfig({
        senderEmail: '',
        senderDomain: '',
        verified: false,
        postPurchaseEnabled: false,
        abandonedCartEnabled: false,
      });
    } finally {
      setIsLoading(false);
    }
  }, [shop, fetchWithAuth]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const toggleEmailType = useCallback(async (type: 'postPurchase' | 'abandonedCart', enabled: boolean) => {
    if (!shop) return;

    // Create a default config if none exists
    const currentConfig = config || {
      senderEmail: '',
      senderDomain: '',
      verified: false,
      postPurchaseEnabled: false,
      abandonedCartEnabled: false,
    };

    setIsSaving(true);
    try {
      await fetchWithAuth('/api/email/settings', {
        method: 'POST',
        body: {
          senderEmail: currentConfig.senderEmail || null,
          senderDomain: currentConfig.senderDomain || null,
          postPurchaseEnabled: type === 'postPurchase' ? enabled : currentConfig.postPurchaseEnabled,
          abandonedCartEnabled: type === 'abandonedCart' ? enabled : currentConfig.abandonedCartEnabled,
          hideSentEmails: currentConfig.hideSentEmails,
        },
      });

      setConfig(prev => {
        const base = prev || currentConfig;
        return {
          ...base,
          postPurchaseEnabled: type === 'postPurchase' ? enabled : base.postPurchaseEnabled,
          abandonedCartEnabled: type === 'abandonedCart' ? enabled : base.abandonedCartEnabled,
        };
      });
    } catch (err) {
      console.error('Error saving email settings:', err);
      setError(t('email.errorSaving'));
    } finally {
      setIsSaving(false);
    }
  }, [shop, config, fetchWithAuth]);

  if (isLoading) {
    return (
      <Card>
        <Box padding="400">
          <InlineStack align="center" blockAlign="center" gap="200">
            <Spinner size="small" />
            <Text as="p">{t('common.loading')}</Text>
          </InlineStack>
        </Box>
      </Card>
    );
  }

  // Show email configuration directly - no need for a separate setup page
  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">{t('email.title')}</Text>
            <Text as="p" tone="subdued">
              {t('email.recommendationsDesc')}
            </Text>
          </BlockStack>
          <Badge tone={config?.verified ? "success" : "attention"}>
            {config?.verified ? t('email.configured') : t('email.setupRequired')}
          </Badge>
        </InlineStack>

        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            <p>{error}</p>
          </Banner>
        )}

        {config?.senderEmail && config?.verified && (
          <Box background="bg-surface-secondary" padding="300" borderRadius="200">
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="100">
                <Text as="span" variant="bodyMd" fontWeight="semibold">
                  {t('email.senderEmail')}
                </Text>
                <Text as="span" variant="bodySm">{config.senderEmail}</Text>
              </BlockStack>
              <InlineStack gap="200" blockAlign="center">
                <Badge tone="success">{t('email.verified')}</Badge>
              </InlineStack>
            </InlineStack>
          </Box>
        )}

        <Divider />

        <BlockStack gap="300">
          <Text as="h3" variant="headingSm">{t('email.emailTypes')}</Text>
          
          <Checkbox
            label={t('email.postPurchase')}
            helpText={t('email.postPurchaseHelp')}
            checked={config?.postPurchaseEnabled ?? false}
            onChange={(checked) => toggleEmailType('postPurchase', checked)}
            disabled={isSaving}
          />

          <Checkbox
            label={t('email.abandonedCart')}
            helpText={t('email.abandonedCartHelp')}
            checked={config?.abandonedCartEnabled ?? false}
            onChange={(checked) => toggleEmailType('abandonedCart', checked)}
            disabled={isSaving}
          />
        </BlockStack>

        {(config?.postPurchaseEnabled || config?.abandonedCartEnabled) && (
          <>
            <Divider />
            <Banner tone="success">
              <Text as="p" variant="bodySm">
                ✅ {t('email.emailActive')} <strong>{config?.senderEmail || 'noreply@nexusai.com'}</strong>.
              </Text>
            </Banner>
          </>
        )}
      </BlockStack>
    </Card>
  );
}
